<template>
    <div>
        <b-spinner v-if="!workflow" centered />

        <div :key="workflow.uuid" v-else>
            <template>
                <b-navbar>
                    <b-navbar-nav>
                        <b-nav-item-dropdown :text="'Preview: ' + settings.version">
                            <b-dropdown-item v-for="s in workflow.settings" :key="s.version"
                                @click="reviewVersion(s.version)">{{ s.version }}</b-dropdown-item>
                            <b-dropdown-divider />
                            <b-dropdown-item @click="removeVersion()">{{ $t('remove') }}</b-dropdown-item>
                            <b-dropdown-item @click="forkVersionModal()">{{ $t('fork') }}</b-dropdown-item>
                        </b-nav-item-dropdown>
                        <b-nav-item-dropdown :text="'Dev: ' + devVersion">
                            <b-dropdown-item v-for="s in workflow.settings" :key="s.version"
                                @click="publishDevVersion(s.version)">{{ s.version }}</b-dropdown-item>
                            <b-dropdown-divider />
                            <b-dropdown-item @click="publishDevVersion('')" v-if="workflow.dev_version != ''"> {{
                                $t('deactivate') }} </b-dropdown-item>
                        </b-nav-item-dropdown>
                        <b-nav-item-dropdown :text="'Live: ' + liveVersion">
                            <b-dropdown-item v-for="s in workflow.settings" :key="s.version"
                                @click="publishLiveVersion(s.version)">{{ s.version }}</b-dropdown-item>
                            <b-dropdown-divider />
                            <b-dropdown-item @click="publishLiveVersion('')" v-if="workflow.public_version != ''"> {{
                                $t('unpublish') }} </b-dropdown-item>
                        </b-nav-item-dropdown>
                    </b-navbar-nav>

                    <b-button variant="success" @click="save"> {{ $t('save') }}</b-button>
                    <b-button variant="danger" @click="remove" class="ml-2" v-if="canRemove"> {{ $t('remove') }}</b-button>
                </b-navbar>

                <Events v-model="settings.nodes" :eventNames="events" :key="settings.version" disableDryRun />

                <b-modal v-model="fork.modal" title="Fork version" @ok="forkVersion()" ok-title="Fork" ok-only>
                    <b-form-group :label="`${this.$t('fromVersion')}:`" :horizontal="true">
                        {{ fork.from }}
                    </b-form-group>

                    <b-form-group :label="`${this.$t('newVersion')}:`" :horizontal="true">
                        <b-input v-model="fork.to" />
                    </b-form-group>
                </b-modal>
            </template>
        </div>
    </div>
</template>

<script>
import Events from '../../components/base/Settings/Events'
import _ from 'lodash'
import VSelect from 'vue-select'
import platformEvents from './platform-events.json'

export default {
    name: 'Workflow',
    components: {
        Events,
        VSelect
    },

    data: function () {
        return {
            workflow: null,
            inspectVersion: null,
            fork: {
                from: null,
                to: null
            },
            settingsBar: false,

        }
    },

    computed: {
        events() {
            let integrations = this.$store.getters.getIntegrations
            let events = integrations.map(i => {
                return i.template.workflowevents
            })

            return _.union(...events).concat(platformEvents)
        },

        currentVersion() {
            return this.inspectVersion || this.settings.version
        },

        settings() {
            const settings = _.get(this.workflow, 'settings', [])
            let version = this.workflow.dev_version
            if (this.$store.getters.isLiveEnv) {
                version = this.workflow.public_version
            }

            if (version == '') {
                return settings[settings.length - 1]
            }

            if (this.inspectVersion) {
                version = this.inspectVersion
            }

            return settings.find(s => {
                return s.version == version
            })
        },

        liveVersion() {
            return this.workflow.public_version || "not live"
        },

        devVersion() {
            return this.workflow.dev_version || "not active"
        },

        canRemove() {
            return this.workflow.integrator == "platform"
        }
    },

    methods: {
        save() {
            const popup = this.$loadingPopup()
            return new Promise((resolve, reject) => {
                this.$workflowApi.patch("/workflow/" + this.$route.params.id, this.workflow).then(resp => {
                    resolve(resp)
                }).catch(err => {

                    this.$bvToast.toast(this.$t('unexpectedError'), {
                        variant: 'danger',
                        title: "Error"
                    })


                    reject(err)
                }).finally(popup.hide)
            })
        },

        async remove() {
            let resp = await this.$bvModal.msgBoxConfirm(`${this.$t('areYouSure')}?`)
            if (!resp) return

            const loader = this.$loadingPopup()

            this.$workflowApi.delete("/workflow/" + this.$route.params.id).then(resp => {
                this.$router.push("/admin/dashboard")
                this.$store.commit("DELETE_WORKFLOW", this.$route.params.id)
            }).catch(err => {
                this.$debugLog(err)

                if (err.response) {
                    if (err.response.status == 409) {
                        this.$bvToast.toast(this.$t("unableToRemoveWorkflow"), {
                            variant: 'danger',
                            title: this.$t('error'),
                            autoHideDelay: 10000,
                            appendToast: false
                        })

                        return
                    }
                }

                this.$bvToast.toast(this.$t('unexpectedError'), {
                    variant: 'danger',
                    title: "Error"
                })
            }).finally(() => {
                loader.hide()
            })
        },

        reviewVersion(version) {
            this.inspectVersion = version
        },

        publishDevVersion(v) {
            let message = "You are about to deploy " + v + " to dev environment. Are you sure?"
            if (v == '') {
                message = "You are about to deactivate workflow from dev environment, this action will also unpublish app if it is live. Are you sure?"
            }

            this.$bvModal.msgBoxConfirm(message).then(resp => {
                if (resp) {
                    this.workflow.dev_version = v
                    if (v == '') {
                        this.workflow.public_version = v
                    }

                    this.save()
                }
            })
        },

        publishLiveVersion(v) {
            let message = "You are about to deploy " + v + " to live environment. Are you sure?"
            if (v == '') {
                message = "You are about to unpublish workflow from live environment. Are you sure?"
            }

            this.$bvModal.msgBoxConfirm(message).then(resp => {
                if (resp) {
                    this.workflow.public_version = v
                    this.save()
                }
            })
        },

        removeVersion() {
            if (this.devVersion == this.currentVersion || this.liveVersion == this.currentVersion) {

                this.$bvToast.toast(this.$t('workflow.cantRemovePublishedVersion'), {
                    variant: 'danger',
                    title: "Error"
                })

                return
            }

            this.$bvModal.msgBoxConfirm("You are about to remove version " + this.currentVersion + ". Are you sure?").then(resp => {
                if (!resp) return

                if (this.workflow.settings.length < 2) {

                    this.$bvToast.toast(this.$t('workflow.cantRemoveLastVersion'), {
                        variant: 'danger',
                        title: "Error"
                    })

                    return
                }

                this.workflow.settings = this.workflow.settings.filter(s => {
                    return s.version != this.currentVersion
                })

                this.save().then(() => {
                    this.inspectVersion = this.devVersion
                })
            })

        },

        forkVersionModal() {
            this.fork.modal = true
            this.fork.from = this.inspectVersion || this.settings.version
        },

        forkVersion() {
            let fork = this.workflow.settings.find(s => {
                return s.version == this.fork.from
            })

            let newFork = _.cloneDeep(fork)
            newFork.version = this.fork.to
            this.workflow.settings.push(newFork)

            this.save().then(() => {
                this.inspectVersion = this.fork.to
            })
        }
    },

    created() {
        if (this.$store.getters.isLiveEnv) {
            this.$router.push("/admin/dashboard")
        }
    },

    mounted() {
        this.$workflowApi.get("/workflow/" + this.$route.params.id).then(resp => {
            this.workflow = resp.data
        }).catch(err => {
            this.$bvToast.toast(this.$t('unexpectedError'), {
                variant: 'danger',
                title: "Error"
            })
        })

        this.$store.dispatch('fetchRoles').then(roles => {
            this.roles = roles
        })
    }
}
</script>